import type { PostCarouselMediaItem } from "../../../../services/postService";
import type { DraftPost, SocialPostData } from "../types";
import { INSTAGRAM_CAROUSEL_MIN_PUBLISH_SLIDES } from "./instagramCarouselLimits";
import { draftPostToSchedulerShape } from "./draftToSchedulerShape";

/**
 * Row shape for `saveChatPosts` / batch create.
 *
 * Carousel fields (`post_type`, `carousel_media`) are written only when there are
 * at least `INSTAGRAM_CAROUSEL_MIN_PUBLISH_SLIDES` slides, matching what we send
 * on publish — avoids orphan CAROUSEL rows with a single child in Dynamo.
 */
export type ChatPostSaveRow = {
  platform: string;
  title: string;
  content: string;
  image_url: string;
  video_url?: string;
  hashtags: string[];
  call_to_action: string;
  scheduled_time: string;
  /** IANA zone the schedule was picked in; empty when unscheduled or legacy. */
  scheduled_timezone?: string;
  post_type?: string;
  carousel_media?: PostCarouselMediaItem[];
  carousel_slides?: Array<{ url: string; type?: string; caption?: string }>;
  /** LinkedIn only: which connected profile to target (see DraftPost.linkedinTarget). */
  linkedinTarget?: "personal" | "business";
};

export function draftPostToChatSaveRow(draft: DraftPost): ChatPostSaveRow {
  const scheduler = draftPostToSchedulerShape(draft);
  const carousel_media =
    scheduler.post_type === "CAROUSEL" && scheduler.carousel_media
      ? scheduler.carousel_media
      : undefined;
  const includeCarousel =
    carousel_media && carousel_media.length >= INSTAGRAM_CAROUSEL_MIN_PUBLISH_SLIDES;

  return {
    platform: draft.platform,
    title: draft.title,
    content: draft.content,
    image_url: draft.image_url,
    video_url: draft.video_url,
    hashtags: draft.hashtags,
    call_to_action: draft.call_to_action,
    scheduled_time: draft.scheduled_time ? draft.scheduled_time : "",
    scheduled_timezone: draft.scheduled_timezone || "",
    ...(draft.linkedinTarget ? { linkedinTarget: draft.linkedinTarget } : {}),
    ...(includeCarousel
      ? {
          post_type: "CAROUSEL" as const,
          carousel_media: carousel_media as PostCarouselMediaItem[],
        }
      : {}),
    ...(Array.isArray(draft.carousel_slides) && draft.carousel_slides.length > 0
      ? { carousel_slides: draft.carousel_slides }
      : {}),
  };
}

/** Persist a legacy `SocialPostData` row (e.g. schedule modal) with optional carousel. */
export function socialPostDataToChatSaveRow(
  postData: SocialPostData,
  scheduledTimeIso: string,
  /** IANA zone the schedule was picked in. */
  scheduledTimezone?: string
): ChatPostSaveRow {
  const hashtags = postData.hashtags
    ? postData.hashtags.split(/[,\s]+/).map((t) => t.trim()).filter(Boolean)
    : [];
  const cm = postData.carousel_media;
  const includeCarousel =
    Array.isArray(cm) && cm.length >= INSTAGRAM_CAROUSEL_MIN_PUBLISH_SLIDES;
  return {
    platform: postData.platforms?.[0] || "facebook",
    title: postData.title || "",
    content: postData.content || "",
    image_url: postData.image_url || "",
    video_url: postData.video_url,
    hashtags,
    call_to_action: postData.call_to_action || "",
    scheduled_time: scheduledTimeIso,
    scheduled_timezone: scheduledTimezone || postData.scheduled_timezone || "",
    ...(includeCarousel
      ? {
          post_type: "CAROUSEL" as const,
          carousel_media: cm as PostCarouselMediaItem[],
        }
      : {}),
    ...(Array.isArray(postData.carousel_slides) && postData.carousel_slides.length > 0
      ? { carousel_slides: postData.carousel_slides }
      : {}),
  };
}
